import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';

import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { formatDate } from '@src/app/utils/date';
import { ITimelineEventElement, ITimelineEventItem, ITimelineProps, ITimelineRouteElement, ITimelineRouteItem } from '@src/app/interfaces/reports/timeline.interface';
import { BusinessEvent } from '@commons/models/domain/BusinessEvent';
import { IRouteEventReportData } from '@src/app/interfaces/report-route.interface';
import { Consts } from '@commons/consts/consts';

@Component({
  selector: 'app-report-route-day-timeline',
  standalone: true,
  imports: [CommonModule, TooltipModule, TranslateModule],
  templateUrl: './report-route-day-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./report-route-day-timeline.component.scss'],
})
export class ReportRouteDayTimelineComponent {

  @Input('data') public set props(data: ITimelineProps) {
    setTimeout(() => {
      this.setRoutesInTimeline(data);
      this.setEventInTimeline(data);
      this.changeDetRef.detectChanges();
    }, 0);
  }

  public day_minutes: string[] = [];
  public routesDisplayInTimeline: ITimelineRouteElement[] = [];
  public eventsDisplayInTimeline: ITimelineEventElement[] = [];

  public readonly VALUE_SIGN = Consts;

  constructor(
    private changeDetRef: ChangeDetectorRef,
    private translate: TranslateService,
  ) {
    this.day_minutes = this.generateMinutesForDiv();
  }

  public trackByEvent(_: number, event: ITimelineEventElement): number {
    return event.eventData.Id;
  }

  public tooltipEventBuilder(event: ITimelineEventElement): string {
    if (event.eventAction === Consts.EventFuelRefuelling) {
      return `${ this.translate.instant('reports.fuelDate') }: ${ event.startHour } | ${ this.translate.instant('reports.fuelQuanitity') }: ${ event.eventData.ED?.V?.toFixed(2) }L`;
    }
    if (event.eventAction === Consts.EventFuelLoss) {
      return `${ this.translate.instant('reports.fuelDrop') }: ${ event.startHour } | ${this.translate.instant('reports.dropQuantity')}: ${ event.eventData.ED?.V?.toFixed(2) }L`;
    }
    return `${this.translate.instant('reports.dateOfEvent')}: ${ event.startHour }`;
  }

  // Wyswietlenie trasy na niebiesko
  private setRoutesInTimeline(data: ITimelineProps): void {
    const routes = data.routes.map(el => this.formatRouteItem(el));

    for (const item of routes) {
      const elementStart = document.querySelector(`[data-minute="${ item.startHour }"]`);
      const elementEnd = document.querySelector(`[data-minute="${ item.endHour }"]`);

      if (elementStart && elementEnd) {
        const startPosition = this.getPositionFromLeft(elementStart);
        const endPosition = this.getPositionFromLeft(elementEnd);
        const widthRouteOnTimelinePX = endPosition - startPosition;
        this.routesDisplayInTimeline = [
          ...this.routesDisplayInTimeline,
          { 
            startPosition,
            endPosition,
            width: widthRouteOnTimelinePX,
            startHour: item.startHour,
            endHour: item.endHour
          }
        ];
      }
    }
  }

  // Wyswietlenie eventów tankowanie/ubytek
  private setEventInTimeline(data: ITimelineProps): void {
    const events = data.events.map(el => this.formatEventItem(el));

    for (const item of events) {
      const elementStart = document.querySelector(`[data-minute="${ item.startHour }"]`);

      if (elementStart) {
        const startPosition = this.getPositionFromLeft(elementStart);
        this.eventsDisplayInTimeline = [...this.eventsDisplayInTimeline, { startPosition, ...item }];
      }
    }
  }

  private getPositionFromLeft(element: Element): number {
    const parent = element.parentElement!;
    const elementRect = element.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const relativeLeft = elementRect.left - parentRect.left;
    return relativeLeft;
  }

  private generateMinutesForDiv() {
    const result = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute++) {
        if (hour === 0 && minute === 0) continue;
        const formattedHour = hour < 10 ? '0' + hour : hour.toString();
        const formattedMinute = minute < 10 ? '0' + minute : minute.toString();
        result.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return result;
  }

  private formatRouteItem(data: IRouteEventReportData): ITimelineRouteItem {
    const startHour = formatDate('HH:mm', new Date(data.St));
    const endHour = data.St && formatDate('HH:mm', new Date(data.En));

    return {
      name: '',
      startHour,
      endHour,
    }
  }

  private formatEventItem(data: BusinessEvent): ITimelineEventItem {
    const startHour = formatDate('HH:mm', new Date(data.TS));

    return {
      name: '',
      startHour,
      eventType: data.ET,
      eventAction: data.EA,
      eventData: {...data},
    }
  }

}

